import { Component, effect, input, output, signal } from '@angular/core';
import { Option } from '../../types/option';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'juyo-option-toggle',
    styleUrl: 'option-toggle.scss',
    imports: [FormsModule],
    template: `
        <div
            class="option-toggle"
            [class.disabled]="disabled()"
            [style.width.px]="widthInPixels() ?? null"
            (mouseenter)="onMouseEnter()"
            (mouseleave)="onMouseLeave($event)"
        >
            <div class="header" [class.open]="isOpen()" (click)="onMouseClick()">
                {{ visibleOption()?.label }}
            </div>
            <span class="toggle-icon" (click)="onMouseClick()"
                ><span class="material-symbols-outlined"> arrow_drop_down </span></span
            >
            <div class="panel" [class.hidden]="!isOpen()">
                @if (displaySearchBar()) {
                    <div>
                        <div class="item search-container">
                            <input
                                class="search"
                                type="text"
                                placeholder="Search"
                                [(ngModel)]="searchText"
                                (input)="onSearch()"
                            />
                            @if (searchText.length > 0) {
                                <span class="clear" (click)="onClearSearch()"
                                    ><span class="material-symbols-outlined"> close_small </span></span
                                >
                            }
                        </div>
                    </div>
                }
                @for (option of visibleOptions(); track option.key) {
                    <div class="option-item" [title]="option.label" (click)="onOptionClick(option)">
                        {{ option.label }}
                    </div>
                }
            </div>
        </div>
    `,
})
export class OptionToggleComponent {
    public selectedOption = input<Option>();
    public options = input.required<Option[]>();
    public disabled = input<boolean>(false);
    public displaySearchBar = input<boolean>(false);
    public openOnClick = input<boolean>(false);
    public widthInPixels = input<number>();

    public optionClick = output<Option>();

    public visibleOption = signal<Option | undefined>(undefined);
    public isOpen = signal<boolean>(false);
    public visibleOptions = signal<Option[]>([]);
    public searchText = '';

    private closeTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        effect(() => {
            const initialOption = this.selectedOption();
            if (initialOption) {
                this.visibleOption.set(initialOption);
            }
        });

        effect(() => {
            const options = this.options();
            if (options) {
                if (this.displaySearchBar()) {
                    this.onSearch();
                } else {
                    this.visibleOptions.set(options.filter((item) => item.key !== NOT_SELECTABLE_KEY));
                }
            }
        });
    }

    public onOptionClick(option: Option) {
        this.visibleOption.set(option);
        this.optionClick.emit(option);
        this.isOpen.set(false);
    }

    public onMouseClick() {
        if (!this.disabled() && this.openOnClick()) {
            this.isOpen.set(true);
        }
    }

    public onMouseEnter() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
        if (!this.disabled() && !this.openOnClick()) {
            this.isOpen.set(true);
        }
    }

    public onMouseLeave(event: MouseEvent) {
        this.closeTimeout = setTimeout(() => {
            this.isOpen.set(false);
            this.closeTimeout = null;
        }, 100);
    }

    public onSearch() {
        let visibleOptions: Option[] = [];
        if (this.searchText) {
            visibleOptions = this.options().filter((item) =>
                item.label.toLowerCase().includes(this.searchText.toLowerCase()),
            );
        } else {
            visibleOptions = this.options().filter((item) => item.key !== NOT_SELECTABLE_KEY);
        }
        this.visibleOptions.set(visibleOptions);
    }

    public onClearSearch(): void {
        this.searchText = '';
        this.visibleOptions.set(this.options().filter((item) => item.key !== NOT_SELECTABLE_KEY));
    }
}

export const NOT_SELECTABLE_KEY = 'NOT_SELECTABLE_KEY';
